import { useMemo, type FC } from "react";

import { formatExhibitLabel } from "../../caseFiles/exhibitLabels";
import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import { DraftPane } from "./DraftPane";
import { EditorEmptyState } from "./EditorEmptyState";
import { FileCardPane } from "./FileCardPane";
import { FilePane } from "./FilePane";
import { FormPane } from "./FormPane";
import { KeyFactsPane } from "./KeyFactsPane";
import { PacketPane } from "./PacketPane";
import { PlanPane } from "./PlanPane";
import { SettingsPane } from "./SettingsPane";
import { CompanyPane } from "./CompanyPane";
import { TimePane } from "./TimePane";
import { WorkflowsPane } from "../workflows/components/WorkflowsPane";

/**
 * The center pane: whatever tab is active, rendered by kind. Each pane owns
 * its own loading and pushes the reader's text selection into the IDE context.
 */
export const EditorArea: FC = () => {
  const { activeArtifact } = useCaseIde();
  const { fs, workspace } = useCaseData();

  const node = useMemo(
    () =>
      activeArtifact?.kind === "file"
        ? fs.nodes.find((n) => n.id === activeArtifact.id) ?? null
        : null,
    [activeArtifact, fs.nodes]
  );

  if (!activeArtifact) return <EditorEmptyState />;

  switch (activeArtifact.kind) {
    case "draft":
      return <DraftPane draftId={activeArtifact.id} />;
    case "file":
      return (
        <FilePane
          node={node}
          label={node ? formatExhibitLabel(fs.labels[node.id], fs.scheme) : ""}
          page={activeArtifact.page}
        />
      );
    case "card":
      return (
        <FileCardPane
          nodeId={activeArtifact.id}
          title={activeArtifact.title}
          exhibitLabel={formatExhibitLabel(
            fs.labels[activeArtifact.id],
            fs.scheme
          )}
        />
      );
    case "packet": {
      const status = workspace.sections.find((s) => s.id === "packet")?.items[0]
        ?.status;
      return (
        <PacketPane
          key={activeArtifact.id}
          packetId={activeArtifact.id}
          ready={status === "ready" || status === "stale"}
          page={activeArtifact.page}
        />
      );
    }
    case "form":
      return <FormPane key={activeArtifact.id} formId={activeArtifact.id} />;
    case "settings":
      return <SettingsPane />;
    case "key_facts":
      return <KeyFactsPane />;
    case "time":
      return <TimePane />;
    case "company":
      return <CompanyPane />;
    case "workflows":
      return <WorkflowsPane />;
    case "plan":
      return <PlanPane key={activeArtifact.id} planId={activeArtifact.id} />;
    default:
      return <EditorEmptyState />;
  }
};
